import { HStack, NativeSelect, Text } from '@chakra-ui/react'
import React from 'react'

import { useReportingI18n } from '../../../i18n'
import {
  HOLD_ALERT_THRESHOLD_OPTIONS_WITH_LABELS,
  REFRESH_INTERVAL_OPTIONS_WITH_LABELS,
} from '../constants'
import type { QueueDashboardSettings } from '../types'

export interface SettingsControlsProps {
  settings: QueueDashboardSettings
  onSettingsChange: (updates: Partial<QueueDashboardSettings>) => void
  compact?: boolean
}

export const SettingsControls: React.FC<SettingsControlsProps> = ({
  settings,
  onSettingsChange,
  compact = false,
}) => {
  const { text } = useReportingI18n()
  const handleRefreshIntervalChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = parseInt(e.target.value, 10)
      if (!isNaN(value)) {
        onSettingsChange({ refreshInterval: value })
      }
    },
    [onSettingsChange],
  )

  const handleHoldAlertThresholdChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = parseInt(e.target.value, 10)
      if (!isNaN(value)) {
        onSettingsChange({ holdAlertThreshold: value })
      }
    },
    [onSettingsChange],
  )

  const selectSize = compact ? 'xs' : 'sm'
  const fontSize = compact ? '0.75rem' : 'sm'

  return (
    <HStack gap={compact ? 3 : 4} flexWrap="wrap">
      <HStack gap={1}>
        <Text fontSize={fontSize} color="fg.muted" whiteSpace="nowrap">
          {text('queueDashboard.settings.alertHoldTime', 'Alert Hold Time')}
        </Text>
        <NativeSelect.Root size={selectSize} width={compact ? '80px' : '100px'}>
          <NativeSelect.Field
            value={settings.holdAlertThreshold}
            onChange={handleHoldAlertThresholdChange}
            aria-label={text(
              'queueDashboard.settings.alertHoldTimeAriaLabel',
              'Select hold alert threshold',
            )}
            data-testid="hold-alert-threshold-select"
            fontSize={fontSize}
            bg="bg.default"
          >
            {HOLD_ALERT_THRESHOLD_OPTIONS_WITH_LABELS.map((option) => (
              <option key={option.value} value={option.value}>
                {compact
                  ? text(
                      'queueDashboard.settings.secondsShort',
                      '{{count}} sec.',
                      {
                        count: option.value,
                      },
                    )
                  : text(
                      'queueDashboard.settings.seconds',
                      '{{count}} seconds',
                      {
                        count: option.value,
                      },
                    )}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </HStack>

      <HStack gap={1}>
        <Text fontSize={fontSize} color="fg.muted" whiteSpace="nowrap">
          {text('queueDashboard.settings.refreshEvery', 'Refresh every')}
        </Text>
        <NativeSelect.Root size={selectSize} width={compact ? '80px' : '100px'}>
          <NativeSelect.Field
            value={settings.refreshInterval}
            onChange={handleRefreshIntervalChange}
            aria-label={text(
              'queueDashboard.settings.refreshAriaLabel',
              'Select refresh interval',
            )}
            data-testid="refresh-interval-select"
            fontSize={fontSize}
            bg="bg.default"
          >
            {REFRESH_INTERVAL_OPTIONS_WITH_LABELS.map((option) => (
              <option key={option.value} value={option.value}>
                {compact
                  ? text(
                      'queueDashboard.settings.secondsShort',
                      '{{count}} sec.',
                      {
                        count: option.seconds,
                      },
                    )
                  : text(
                      'queueDashboard.settings.seconds',
                      '{{count}} seconds',
                      {
                        count: option.seconds,
                      },
                    )}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </HStack>
    </HStack>
  )
}

export default SettingsControls
